import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { DEFAULT_AVATAR_URL } from '../shared/constants';
import { IEmployee, ITokenResponse } from '../shared/models';
import { ConfigService } from '../shared/services/config.service';
import { AuthUserPayload } from '../shared/models/auth.model';
import { toPermissions } from '../shared/utils/permissions.util';
import { Permission } from '../shared/models/permission.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly httpClient = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly configService = inject(ConfigService);

  constructor() {
    this.isUserLogIn = this.isUserLogIn.bind(this);
  }

  private readonly urlBase: string = '/api/auth';
  private readonly authPayload = signal<AuthUserPayload | null>(null);
  public readonly user = computed<IEmployee | null>(() => this.authPayload()?.employee);
  public userId = computed(() => this.user()?.id);
  public userAvatar = computed(() => this.user()?.avatarUrl || DEFAULT_AVATAR_URL);

  public userPermissions = computed(() => toPermissions(this.authPayload()?.permissions || []));

  public getUser(): IEmployee | null {
    return this.user();
  }

  public setUser(employee: IEmployee): void {
    // TODO this needs to be revised
    this.authPayload.update((prev) => ({ ...prev, employee }));
  }

  logInWithPassword({ email, password }: { email: string; password: string }) {
    return this.httpClient.post<AuthUserPayload>(`${this.urlBase}/login`, { email, password });
  }

  logOut(): void {
    this.httpClient.post(`${this.urlBase}/logout`, {}).subscribe({
      next: () => console.log('Refresh token cleared on backend'),
      error: (err) => console.error('Backend logout failed', err),
    });
    this.authPayload.set(null);

    this.router.navigate(['login']);
  }

  logOutOnExpiredRefreshToken(): void {
    this.authPayload.set(null);
    this.router.navigate(['login']);
  }

  refreshToken(): Observable<ITokenResponse> {
    return this.httpClient
      .post<ITokenResponse>(`${this.urlBase}/refresh`, {})
      .pipe(tap({ error: () => this.logOutOnExpiredRefreshToken() }));
  }

  refreshMyDetails(): Observable<AuthUserPayload> {
    return this.httpClient
      .get<AuthUserPayload>(`${this.urlBase}`)
      .pipe(tap((authPayload) => this.authPayload.set(authPayload)));
  }

  getMyDetails(): Observable<AuthUserPayload> {
    return this.refreshMyDetails().pipe(
      tap({
        next: () => this.configService.loadConfiguration(),
        error: () => this.authPayload.set(null),
      }),
    );
  }

  isUserLogIn(): boolean {
    return !!this.user();
  }

  hasPermission(permissions: Permission[]): boolean {
    if (!permissions.length) {
      return true;
    }
    return this.userPermissions().some((permission) => permissions.includes(permission)) ?? false;
  }

  isLead(): boolean {
    return this.user()?.lead;
  }

  public hasOfficeSpaceAccess(): boolean {
    const user = this.getUser();
    return (
      user?.projects?.some(
        (project) =>
          project.projectName.toLowerCase() === 't&c' ||
          project.projectName.toLowerCase().includes('dms'),
      ) || false
    );
  }

  async handleSuccessfulLogin(response: AuthUserPayload) {
    this.authPayload.set(response);

    try {
      await this.configService.loadConfiguration();
    } catch (error) {
      throw new Error('Failed to load configuration');
    }

    if (!this.route.snapshot.queryParams.redirectTo) {
      await this.router.navigate(['/']);
      return;
    }

    const redirectUrl = new URL(this.route.snapshot.queryParams.redirectTo, location.origin);

    await this.router.navigate([redirectUrl.pathname], {
      queryParams: Object.fromEntries(redirectUrl.searchParams.entries()),
    });
  }
}
